/* eslint-disable lingui/no-unlocalized-strings, unicorn/no-document-cookie */
// Cookie serialization strings are not user-facing; direct document.cookie
// is the portable API (cookie-store isn't in Safari yet).
import { useEffect } from "react";

/**
 * Captures `?ref=<userId>` query parameter into a first-party cookie so the
 * next register submit can pick it up and credit the referrer. Runs once
 * on mount; strips the param from the URL after storing it to keep the
 * address bar clean.
 *
 * Cookie is:
 *   name:      apply_edge_ref
 *   ttl:       90 days (plenty of funnel time; auto-expires)
 *   sameSite:  Lax (so the cookie survives clicks from external sites)
 *   path:      /
 *   secure:    true in prod (served over HTTPS)
 *
 * Consumed in apps/client/src/services/auth/register.ts at signup time.
 */
const COOKIE_NAME = "apply_edge_ref";
const COOKIE_TTL_DAYS = 90;

export const useReferralCapture = () => {
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      if (!ref) return;
      // Basic sanity: reject obviously-bogus values so the server doesn't
      // have to deal with giant strings in the register payload.
      if (ref.length < 5 || ref.length > 64) return;

      const expires = new Date();
      expires.setDate(expires.getDate() + COOKIE_TTL_DAYS);
      const secure = window.location.protocol === "https:" ? "; secure" : "";
      document.cookie = `${COOKIE_NAME}=${encodeURIComponent(ref)}; expires=${expires.toUTCString()}; path=/; samesite=lax${secure}`;

      // Remove ?ref from the URL so it doesn't get shared/linked onwards
      params.delete("ref");
      const next = `${window.location.pathname}${params.toString() ? `?${params}` : ""}${window.location.hash}`;
      window.history.replaceState({}, "", next);
    } catch {
      // Intentionally swallow — referral tracking is best-effort
    }
  }, []);
};

/**
 * Read the referral cookie. Returns null if the user arrived without a
 * ref cookie. Safe to call server-side (returns null) and client-side.
 */
export const getStoredReferrerId = (): string | null => {
  if (typeof document === "undefined") return null;
  const match = new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]+)`).exec(document.cookie);
  return match ? decodeURIComponent(match[1]) : null;
};
